"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Game = exports.GameState = void 0;
const tslib_1 = require("tslib");
const shuffle_1 = require("./shuffle");
const utils_1 = require("./utils");
const types_1 = require("./types");
const constants_1 = require("./constants");
const rules_1 = require("./rules");
const hash_1 = (0, tslib_1.__importDefault)(require("./hash"));
const util_1 = require("util");
const _1 = require(".");
const JOKER = "joker";
function createDeck({ numJokers = 2, values = constants_1.CARD_ORDER, suits = constants_1.SUITS }) {
    const cards = values.filter((face) => face !== JOKER);
    const deck = suits.flatMap((suit, i) => {
        return cards.map((value, j) => {
            return {
                _id: (0, hash_1.default)(i * cards.length + j + Math.random() + ""),
                value,
                suit
            };
        });
    });
    for (let i = 0; i < numJokers; i++) {
        deck.push({
            _id: (0, hash_1.default)(deck.length + Math.random() + ""),
            value: JOKER,
            suit: JOKER
        });
    }
    return deck;
}
var GameState;
(function (GameState) {
    GameState["DEAL"] = "DEAL";
    GameState["SWAP"] = "SWAP";
    GameState["START_ROUND"] = "START_ROUND";
    GameState["PLAY"] = "PLAY";
    GameState["END_ROUND"] = "END_ROUND";
    GameState["FINISH"] = "FINISH";
})(GameState = exports.GameState || (exports.GameState = {}));
function createTurnId(turnNumber) {
    return "" + turnNumber;
}
class Game {
    constructor(gameId, opts, onChange = null) {
        var _a, _b;
        this.playerRanksMap = new Map();
        this.hasStartCard = false;
        this.logger = (0, util_1.debuglog)("game-engine");
        this._onChange = null;
        this.gameId = gameId;
        this.opts = opts;
        this._onChange = onChange;
        const shouldSwap = ((_a = opts.rules) === null || _a === void 0 ? void 0 : _a.shouldSwap) || ((_b = opts.rules) === null || _b === void 0 ? void 0 : _b.shouldAutoSwap);
        const includeVices = opts.players.length >= 4;
        /**
         * Assign explicit ranks
         */
        opts.players.forEach((playerId, i) => {
            let rank = types_1.RankEnum.unranked;
            if (shouldSwap) {
                if (i === 0) {
                    rank = types_1.RankEnum.president;
                }
                else if (i === this.numPlayers - 1) {
                    rank = types_1.RankEnum.asshole;
                }
                if (includeVices) {
                    if (i === 1) {
                        rank = types_1.RankEnum.vicepresident;
                    }
                    else if (i === this.numPlayers - 2) {
                        rank = types_1.RankEnum.viceasshole;
                    }
                }
            }
            this.playerRanksMap.set(playerId, rank);
        });
        /**
         * Initial state after swap...
         */
        let state = GameState.START_ROUND;
        if (shouldSwap) {
            state = GameState.SWAP;
        }
        const hands = this.deal(shouldSwap);
        let whosNext = opts.players[0];
        if (state !== GameState.SWAP) {
            const startCard = this.startCard;
            this.logger("finding starting card...", { startCard });
            const who = Object.values(hands).find((hand) => {
                const cardIdx = hand.cards.findIndex((card) => card.value === startCard.value && card.suit === startCard.suit);
                return cardIdx > -1;
            });
            this.logger("Start card with", { who });
            if (who) {
                this.hasStartCard = true;
                whosNext = who.player;
            }
        }
        this.gameState = [
            opts.__initialState || {
                _id: Game.createId("init"),
                startTime: 0,
                lastUpdateTime: 0,
                hands,
                whosNext,
                round: 0,
                turns: [],
                lastTurn: [],
                folded: [],
                out: [],
                inPlay: undefined,
                state
            }
        ];
    }
    static createId(source) {
        return (0, hash_1.default)(source);
    }
    get startCard() {
        var _a;
        return (_a = this.opts.startCard) !== null && _a !== void 0 ? _a : { value: 3, suit: "club" };
    }
    get options() {
        return this.opts;
    }
    get full() {
        return this.gameState;
    }
    get curr() {
        return this.gameState[this.gameState.length - 1];
    }
    get lastOutcome() {
        return this.curr.lastOutcome;
    }
    get inPlay() {
        return this.curr.inPlay;
    }
    get state() {
        return this.curr.state;
    }
    get whosNext() {
        return this.curr.whosNext;
    }
    get president() {
        const { players } = this.opts;
        return players[0];
    }
    get players() {
        return this.opts.players;
    }
    get numPlayers() {
        return this.opts.players.length;
    }
    get asshole() {
        const { players } = this.opts;
        return players[players.length - 1];
    }
    getRanks() {
        const curr = this.curr;
        const foldedSet = new Set(curr.folded);
        const ranks = curr.out.filter((playerId) => !foldedSet.has(playerId));
        return ranks;
    }
    isOut(playerId) {
        return playerId && this.curr.out.includes(playerId);
    }
    updateGameState({ _id: _omitId, ...nextStateWithoutId }) {
        const _id = Game.createId(JSON.stringify(nextStateWithoutId));
        if (!nextStateWithoutId.startTime) {
            nextStateWithoutId.startTime = Date.now();
        }
        const nextState = {
            ...nextStateWithoutId,
            lastUpdateTime: Date.now(),
            _id
        };
        this.logger("updateGameState WTF!", { nextState });
        Object.freeze(nextState);
        if (nextState.state === GameState.START_ROUND) {
            this.gameState.push(nextState);
        }
        else {
            this.gameState[this.gameState.length - 1] = nextState;
        }
        if (this._onChange) {
            this._onChange(this);
        }
    }
    deal(shouldSetSwapFlag = false) {
        var _a;
        this.logger("deal");
        const { players, dealer = 0 } = this.opts;
        const numPlayers = players.length;
        const deck = createDeck({
            values: this.opts.quick
                ? constants_1.SHORT_CARD_VALUES
                : ((_a = this.opts.rules) === null || _a === void 0 ? void 0 : _a.twoWinsTrick)
                    ? constants_1.TWO_WINS_CARD_ORDER
                    : constants_1.CARD_ORDER,
            suits: this.opts.quick ? constants_1.SHORT_SUIT_VALUES : constants_1.SUITS,
            numJokers: this.opts.numJokers
        });
        (0, shuffle_1.shuffle)(deck);
        const hands = Array.from({ length: numPlayers }, () => []);
        let dealTo = (dealer + 1) % numPlayers;
        while (deck.length) {
            const card = deck.pop();
            if (card) {
                // Populate deck
                hands[dealTo].push(card);
            }
            // Move around the players
            dealTo = (dealTo + 1) % numPlayers;
        }
        // Sort hands
        return hands.reduce((acc, hand, i) => {
            var _a;
            const player = players[i];
            const rank = (_a = this.playerRanksMap.get(player)) !== null && _a !== void 0 ? _a : null;
            let numSwap = null;
            let swapType = null;
            if (shouldSetSwapFlag) {
                // is pres or ass
                switch (rank) {
                    case types_1.RankEnum.president:
                        swapType = _1.SwapType.low;
                        numSwap = 2;
                        break;
                    case types_1.RankEnum.asshole:
                        swapType = _1.SwapType.high;
                        numSwap = 2;
                        break;
                    case types_1.RankEnum.vicepresident:
                        swapType = _1.SwapType.low;
                        numSwap = 1;
                        break;
                    case types_1.RankEnum.viceasshole:
                        swapType = _1.SwapType.high;
                        numSwap = 1;
                        break;
                    default:
                        numSwap = null;
                }
            }
            acc[player] = {
                player: player,
                numSwap,
                swapType,
                rank,
                cards: (0, utils_1.assignDeltTo)(player, (0, utils_1.sortHand)(hand))
            };
            return acc;
        }, {});
    }
    findWhosNext(afterWho) {
        const whosLeft = this.opts.players.filter((playerId) => !this.curr.out.includes(playerId));
        const whoIdx = whosLeft.indexOf(afterWho);
        const nextIdx = (whoIdx + 1) % whosLeft.length;
        const who = whosLeft[nextIdx];
        this.logger("findWhosNext", {
            whosLeft,
            whoIdx,
            nextIdx,
            who
        });
        return who;
    }
    swap(swapAction) {
        var _a;
        this.logger("actually swapping cards...");
        let nextState = this.curr.state;
        if (this.curr.state !== GameState.SWAP) {
            throw new Error("Cannot swap when game is started!");
        }
        const players = this.players;
        const numPlayers = this.numPlayers;
        const nextSwaps = ((_a = this.curr.swaps) === null || _a === void 0 ? void 0 : _a.slice()) || [];
        let nextHands = this.curr.hands;
        // Remove any existing swap;
        nextSwaps.filter((s) => s.fromPlayer === swapAction.fromPlayer);
        const rank = this.playerRanksMap.get(swapAction.fromPlayer);
        let toRank = -1;
        if (rank === types_1.RankEnum.president) {
            // I am president swapping with asshole
            if (swapAction.cards.length !== 2) {
                throw new Error("Must select 2 cards to swap");
            }
            toRank = numPlayers - 1;
        }
        else if (rank === types_1.RankEnum.vicepresident && numPlayers >= 4) {
            if (swapAction.cards.length !== 1) {
                throw new Error("Must select 1 cards to swap");
            }
            // I am vice-president swapping with vice-asshole
            toRank = numPlayers - 2;
        }
        else if (rank === types_1.RankEnum.asshole) {
            if (swapAction.cards.length !== 2) {
                throw new Error("Must select 2 cards to swap");
            }
            // I am asshole swapping with president
            toRank = 0;
        }
        else if (rank === types_1.RankEnum.viceasshole && numPlayers >= 4) {
            if (swapAction.cards.length !== 1) {
                throw new Error("Must select 1 card to swap");
            }
            // I am vice-asshole swapping with vice-president;
            toRank = 1;
        }
        const finalisedSwap = {
            ...swapAction,
            toPlayer: players[toRank]
        };
        this.logger("Swapping...", {
            currRank: rank,
            toRank,
            ...finalisedSwap
        });
        nextSwaps.push(finalisedSwap);
        const swapsAreComplete = (this.numPlayers < 4 && nextSwaps.length === 2) ||
            (this.numPlayers >= 4 && nextSwaps.length === 4);
        if (swapsAreComplete) {
            // Update the hand(s)
            nextHands = { ...nextHands };
            nextSwaps.forEach((s) => {
                const swapSet = new Set(s.cards.map((c) => c._id));
                const fromHand = nextHands[s.fromPlayer];
                const toHand = nextHands[s.toPlayer];
                // Remove the cards from the swapped hand...
                const nextFromCards = fromHand.cards.slice().filter((c) => {
                    return !swapSet.has(c._id);
                });
                fromHand.cards = nextFromCards;
                fromHand.numSwap = null; // reset the flag;
                const nextToCards = (0, utils_1.sortHand)(toHand.cards.slice().concat(s.cards));
                toHand.cards = nextToCards;
            });
            nextState = GameState.START_ROUND;
        }
        this.updateGameState({
            ...this.curr,
            hands: nextHands,
            swaps: nextSwaps,
            state: nextState
        });
    }
    start(swap) {
        var _a;
        if (swap === void 0) { swap = (_a = this.opts.rules) === null || _a === void 0 ? void 0 : _a.shouldAutoSwap; }
        const nextHands = { ...this.curr.hands };
        this.logger("start");
        if (this.curr.state === GameState.PLAY) {
            /**
             * Game already started!
             */
            return;
        }
        if (swap) {
            this.logger("swapping cards...");
            if (this.curr.state !== GameState.DEAL) {
                throw new Error("Cannot swap when game is started!");
            }
            const pres = nextHands[this.president];
            const nextPres = {
                ...pres,
                cards: pres.cards.slice()
            };
            /**
             * TODO allow user to do this
             */
            const presWorst = nextPres.cards.shift();
            const ass = nextHands[this.asshole];
            const nextAss = {
                ...ass,
                cards: ass.cards.slice()
            };
            /**
             * TODO allow user to do this
             */
            const assBest = nextAss.cards.pop();
            if (assBest) {
                nextPres.cards.push(assBest);
                (0, utils_1.sortHand)(nextPres.cards);
                nextHands[this.president] = nextPres;
            }
            if (presWorst) {
                nextAss.cards.unshift(presWorst);
                (0, utils_1.sortHand)(nextAss.cards);
                nextHands[this.asshole] = nextAss;
            }
            this.updateGameState({
                ...this.curr,
                hands: nextHands,
                whosNext: this.president,
                round: 0,
                turns: [],
                inPlay: undefined,
                state: GameState.SWAP
            });
        }
        // else {
        //   // Find 3 of clubs
        //   let cardIdx = -1;
        //   const startCard =
        //     this.opts.startCard ?? ({ value: 3, suit: "club" } as Card);
        //   this.logger("finding starting card...", { startCard });
        //   const who = Object.values(nextHands).find((hand) => {
        //     cardIdx = hand.cards.findIndex(
        //       (card) =>
        //         card.value === startCard.value && card.suit === startCard.suit
        //     );
        //     return cardIdx > -1;
        //   });
        //   if (cardIdx === -1 || !who) {
        //     this.logger("Start card not found!");
        //     this.updateGameState({
        //       ...this.curr,
        //       hands: nextHands,
        //       whosNext: this.opts.players[0],
        //       round: 0,
        //       state: GameState.PLAY
        //     });
        //     return;
        //   }
        //   const player = who.player;
        //   const turn: InPlay = {
        //     _id: createTurnId(0),
        //     player,
        //     type: "trick",
        //     cards: [who.cards[cardIdx]]
        //   };
        //   const nextCards = [
        //     ...who.cards.slice(0, cardIdx),
        //     ...who.cards.slice(cardIdx + 1)
        //   ];
        //   nextHands[player] = {
        //     ...who,
        //     cards: nextCards
        //   };
        //   this.updateGameState({
        //     ...this.curr,
        //     hands: nextHands,
        //     whosNext: this.findWhosNext(turn.player),
        //     round: 0,
        //     inPlay: turn,
        //     lastTurn: [turn],
        //     turns: [turn],
        //     state: GameState.PLAY
        //   });
        // }
        // swap?
    }
    findCardIdxs(cards, handCards) {
        return cards.map((turnCard) => {
            return handCards.findIndex((handCard) => {
                return handCard._id === turnCard._id;
            });
        });
    }
    assertPlayersTurnInHand(turn, hand) {
        const cardIdxs = this.findCardIdxs(turn.cards, hand.cards);
        if (!cardIdxs.every((idx) => idx > -1)) {
            throw new Error(`Player: ${JSON.stringify(turn.cards)} not found!`);
        }
    }
    playValidTrick(turn, outcome) {
        this.logger("playValidTrick", { turn, outcome });
        const curr = this.curr;
        const { player, cards } = turn;
        const playerHand = curr.hands[player];
        const cardIdxs = this.findCardIdxs(cards, playerHand.cards);
        const nextHands = { ...curr.hands };
        const nextHand = {
            ...playerHand,
            cards: playerHand.cards.filter((_, i) => {
                return !cardIdxs.includes(i);
            })
        };
        nextHands[player] = nextHand;
        let willEndRound = false;
        let nextState = GameState.PLAY;
        let nextOut = curr.out;
        let whosNext = this.findWhosNext(turn.player);
        // Check if player is out
        if (!nextHand.cards.length) {
            this.logger("player out!", { player });
            // Player is out!
            nextOut = [...curr.out, nextHand.player];
            // Asshole found! AND GAME OVER
            if (nextOut.length === this.numPlayers - 1) {
                this.logger("asshole found!", { player: whosNext });
                nextOut.push(nextHands[whosNext].player);
                nextState = GameState.FINISH;
                // No-one is next!
                whosNext = undefined;
            }
            // Player may have won the trick with turn
        }
        else if (outcome === null || outcome === void 0 ? void 0 : outcome.winsTrick) {
            whosNext = turn.player; // Player who won gets to restart
            willEndRound = true;
        }
        if (outcome && outcome.isWild && outcome.wildValue) {
            this.logger("finalising turn: played wildcard in hand");
            /**
             * Player has played a wildcard (i.e Joker)
             * We need to set the `inPlay` card.value(s)
             * to the hand "underneath"
             */
            turn = {
                ...turn,
                __original_turn: turn,
                cards: turn.cards.map((card) => {
                    return {
                        ...card,
                        value: outcome.wildValue
                    };
                })
            };
        }
        const inPlay = {
            ...turn,
            _id: createTurnId(curr.turns.length),
            __outcome: outcome
        };
        this.updateGameState({
            ...curr,
            hands: nextHands,
            out: nextOut,
            lastTurn: [inPlay],
            turns: [...curr.turns, inPlay],
            inPlay,
            lastOutcome: outcome,
            round: curr.round,
            whosNext,
            state: nextState
        });
        if (willEndRound) {
            this.endRound(whosNext);
        }
    }
    playPass(turn) {
        this.logger("playPass", { turn });
        const curr = this.curr;
        const whosNext = this.findWhosNext(turn.player);
        const willEndRound = this.willRestart(turn);
        const finalisedTurn = {
            ...turn,
            _id: createTurnId(curr.turns.length)
        };
        /**
         * If this is a "skip-a-go" moment
         * (i.e. turn.reason === "auto")
         * then we join the last turns
         */
        const lastTurn = turn.reason === "auto"
            ? [...curr.lastTurn, finalisedTurn]
            : [finalisedTurn];
        let nextState = {
            ...curr,
            lastTurn,
            turns: [...curr.turns, finalisedTurn],
            whosNext
        };
        if (willEndRound) {
            nextState = {
                ...nextState,
                note: `Round end, ${whosNext} restarts`,
                state: GameState.END_ROUND
            };
        }
        this.updateGameState(nextState);
        if (willEndRound) {
            this.endRound(whosNext);
        }
    }
    turn(turn) {
        var _a, _b, _c, _d;
        const curr = this.curr;
        if (curr.state === GameState.FINISH) {
            throw new Error("Cannot play turn on inactive game!");
        }
        this.logger("turn", { turn });
        if (turn.type === "trick" && turn.cards) {
            this.assertPlayersTurnInHand(turn, curr.hands[turn.player]);
            // First go of first round
            if (!((_a = this.opts.rules) === null || _a === void 0 ? void 0 : _a.shouldSwap) &&
                !((_b = this.opts.rules) === null || _b === void 0 ? void 0 : _b.shouldAutoSwap) &&
                this.hasStartCard &&
                curr.round === 0 &&
                !curr.turns.length) {
                const hasStartCard = turn.cards.some((c) => {
                    return (c.value === this.startCard.value && c.suit === this.startCard.suit);
                });
                this.logger("hasStartCard?", { hasStartCard, turn });
                if (!hasStartCard) {
                    throw new Error(`Must start with ${this.startCard.value} of ${this.startCard.suit}s`);
                }
            }
            const allCardsInPlay = (0, utils_1.getAllCardsInPlay)(curr.turns);
            const outcome = (0, rules_1.validateTrick)(allCardsInPlay, turn.cards, this.opts.rules);
            /**
             * fourOfAKindRevolution allows players to play-out-of-turn
             */
            const canPlayTurn = (((_c = this.opts.rules) === null || _c === void 0 ? void 0 : _c.fourOfAKindRevolution) &&
                outcome.isRevolution &&
                outcome.isValid) ||
                curr.whosNext === turn.player;
            if (!canPlayTurn) {
                throw new Error("Cannot play out-of-turn!");
            }
            if (!outcome.isValid) {
                throw new Error(`Cannot play ${outcome.reason}`);
            }
            this.playValidTrick(turn, outcome);
            const playerMatchedHand = outcome["outcome"] === "equal";
            if (playerMatchedHand) {
                this.logger("playerMatchedHand");
                if ((_d = this.opts.rules) === null || _d === void 0 ? void 0 : _d.shouldSkip) {
                    this.logger("shouldSkip", { state: this.curr.state });
                    /**
                     * Only add extra pass if the game is still running
                     */
                    if (this.curr.state === GameState.PLAY && this.curr.whosNext) {
                        this.playPass({
                            player: this.curr.whosNext,
                            type: "pass",
                            reason: "auto"
                        });
                    }
                    else {
                        this.logger("not skipping");
                    }
                }
            }
        }
        else if (turn.type === "pass") {
            this.playPass(turn);
        }
    }
    // TODO Fix bug when player has just gone out then a miss-a-go happens to the player before them
    willRestart(turn) {
        var _a, _b;
        const curr = this.curr;
        const whosNext = this.findWhosNext(turn.player);
        if (this.isOut((_a = curr.inPlay) === null || _a === void 0 ? void 0 : _a.player)) {
            const inPlayIdx = curr.turns.findIndex((t) => { var _a; return t._id === ((_a = curr.inPlay) === null || _a === void 0 ? void 0 : _a._id); });
            const firstAfterOut = curr.turns[inPlayIdx + 1];
            if (firstAfterOut &&
                firstAfterOut.type === "pass" &&
                firstAfterOut.player === whosNext) {
                return true;
            }
        }
        return whosNext === ((_b = curr.inPlay) === null || _b === void 0 ? void 0 : _b.player);
    }
    endRound(whosNext) {
        this.logger("endRound", { restartsWith: whosNext });
        const curr = this.curr;
        this.updateGameState({
            ...curr,
            turns: [],
            lastOutcome: undefined,
            inPlay: undefined,
            round: curr.round + 1,
            whosNext,
            state: GameState.START_ROUND
        });
    }
    foldPlayer(playerId) {
        var _a;
        this.logger("foldPlayer", { playerId });
        if (this.opts.players.includes(playerId)) {
            const curr = this.curr;
            let whosNext = curr.whosNext;
            let nextState = curr.state;
            let out = curr.out;
            if (whosNext === playerId) {
                whosNext = this.findWhosNext(whosNext);
            }
            let folded = curr.folded;
            if (!out.includes(playerId)) {
                out = [...out, playerId];
            }
            if (!folded.includes(playerId)) {
                folded = [...folded, playerId];
            }
            const willEndRound = whosNext === ((_a = curr.inPlay) === null || _a === void 0 ? void 0 : _a.player);
            if (out.length === this.numPlayers) {
                nextState = GameState.FINISH;
            }
            this.updateGameState({
                ...curr,
                whosNext,
                state: nextState,
                out,
                folded
            });
            if (willEndRound) {
                this.endRound(whosNext);
            }
        }
    }
    rejoinPlayerIfPossible(playerId) {
        this.logger("rejoinPlayerIfPossible", { playerId });
        const curr = this.curr;
        if (this.opts.players.includes(playerId) &&
            curr.out.includes(playerId) &&
            curr.hands[playerId].cards.length > 0) {
            const nextOut = curr.out.filter((p) => p !== playerId);
            const nextFolded = curr.folded.filter((p) => p !== playerId);
            this.updateGameState({
                ...curr,
                out: nextOut,
                folded: nextFolded
            });
        }
    }
    removeOnChangeListener() {
        this._onChange = null;
    }
}
exports.Game = Game;
//# sourceMappingURL=game.js.map